/**
 * Typed API client for the user-facing support endpoints (PR-12 of the
 * Logora Help & Trust Center plan). Mirrors the structure of supportAdmin.ts
 * but for the user surface (`/api/support/*`).
 *
 * All requests go through the existing httpClient axios instance which adds
 * JWT/CSRF and transparently refreshes the access token; no separate axios
 * client is created here. The base URL is `/api`, so paths must be relative
 * — e.g. `support/tickets/`.
 *
 * The user-facing queryset is filtered to `created_by=request.user` server
 * side, even for staff. Operators must use the admin endpoints separately.
 *
 * Sanitization rules applied by the backend on this surface:
 *   - operator messages serialize the author as the pseudo-user
 *     `{id: null, name: "Поддержка Logora", avatar: null}` — never the
 *     real staff id;
 *   - `dispute_case.evidence[]` only includes `visibility=shared` rows;
 *   - `dispute_case.decision` is sanitized to `decision_type`, `comment`,
 *     `effective_refund_amount`, `refund_currency`, `decided_at` only;
 *   - `linked_expert` is reduced to `id`/name/avatar (no email/phone).
 *
 * Canonical error codes (`ticket_closed`, `dispute_not_open`, etc.) are
 * surfaced via `SupportApiError.code` so the UI can branch.
 */

import type { AxiosError } from 'axios';
import api from './httpClient';
// Re-export so existing imports `from '@/services/support'` keep working
// (e.g. the consultation modal). The canonical leaf-utility lives in
// `utils/extractFieldErrors` to avoid pulling httpClient into UI bundles
// that only need the helper. We also import it locally because wrapError
// below calls it directly — a bare `export ... from` doesn't bring the
// symbol into this module's scope.
import { extractFieldErrors } from '@/utils/extractFieldErrors';
export { extractFieldErrors };

// ---------------------------------------------------------------------------
// Common response shapes
// ---------------------------------------------------------------------------

export interface PublicAuthor {
    id: number | null;
    name: string;
    avatar?: string | null;
}

export interface DRFPaginated<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

// ---------------------------------------------------------------------------
// Enums (kept aligned with backend `support/enums.py`)
// ---------------------------------------------------------------------------

export type TicketStatus =
    | 'open'
    | 'in_progress'
    | 'waiting_user'
    | 'dispute_opened'
    | 'resolved'
    | 'closed';

export type TicketCategory =
    | 'tech'
    | 'payment'
    | 'booking'
    | 'consultation_quality'
    | 'complaint'
    | 'other';

export type TicketContextType =
    // TODO(v2-cutover): 'consultation_booking' is legacy — the
    // consultations.ConsultationBooking model is deleted in the V2 hard cutover
    // and SupportTicket.consultation_booking is dropped (the support context now
    // resolves via order_item/conference). Kept here only until the backend
    // support context layer (support/services/context.py, support/enums.py) and
    // the remaining FE readers (ReportProblemButton, MeetingPage,
    // SupportTicketPage, admin BusinessContextPanel) stop emitting/reading it.
    // The canonical financial anchor is 'order_item'.
    | 'consultation_booking'
    | 'conference'
    | 'legacy_reservation'
    | 'commerce_reservation'
    | 'secure_deal'
    | 'order_item'
    | 'secure_payment'
    | 'secure_refund';

export type MessageAuthorRole = 'user' | 'operator' | 'system';

export type DisputeReason =
    | 'expert_no_show'
    | 'client_no_show'
    | 'expert_late'
    | 'expert_quality'
    | 'session_interrupted'
    | 'not_as_described'
    | 'payment_issue'
    | 'other';

export type DecisionType =
    | 'full_refund'
    | 'partial_refund'
    | 'no_refund'
    | 'reschedule'
    | 'manual_resolution';

/**
 * Canonical reason a dispute cannot be opened from the user side. Returned by
 * the backend as `escalation.dispute_unavailable_reason`. The UI maps this to
 * a tooltip / inline message.
 */
export type DisputeUnavailableReason =
    | 'no_financial_object'
    | 'deadline_unknown'
    | 'deadline_expired'
    | 'already_escalated'
    | 'existing_open_dispute'
    | 'ticket_closed'
    | 'permission_denied';

// ---------------------------------------------------------------------------
// Read shapes
// ---------------------------------------------------------------------------

export interface AttachmentView {
    id: number;
    url: string; // signed/protected
    original_filename: string;
    size_bytes: number;
    content_type: string;
}

export interface MessageView {
    id: number;
    author_role: MessageAuthorRole;
    author: PublicAuthor | null;
    body: string;
    attachments: AttachmentView[];
    created_at: string;
    metadata?: Record<string, unknown>;
}

/**
 * Sanitized fan-out of business/financial FKs for the user-facing detail.
 * Mirrors the dict produced by `TicketDetailSerializer.get_context` —
 * each id present means the ticket is bound to that object. Per ADR-0002 the
 * backend surfaces a stable external identifier for every FK — UUID or
 * brand-prefix `public_id` (e.g. `"oi-…"`) — and never an internal integer
 * pk, so every id here is a string.
 */
export interface TicketContextSummary {
    // V2 hard cutover: the removed ConsultationBooking root is gone — the
    // support context now resolves business/financial roots via
    // ``order_item_id`` and ``conference_id``.
    conference_id: string | null;
    order_item_id: string | null;
    secure_deal_id: string | number | null;
    secure_payment_id: string | number | null;
    secure_refund_id: string | number | null;
    legacy_reservation_id: string | number | null;
    commerce_reservation_id: string | null;
}

export interface TicketSummary {
    public_id: string;
    subject: string;
    category: TicketCategory;
    category_label: string;
    status: TicketStatus;
    status_label: string;
    has_active_dispute: boolean;
    linked_expert: PublicAuthor | null;
    last_message_at: string | null;
    next_response_due_at: string | null;
    is_overdue: boolean;
    created_at: string;
}

/**
 * The user-side dispute evidence serializer collapses the file to just a
 * signed URL string (or null for snapshot evidence). No filename/size/MIME
 * is exposed — the URL is meant for direct download.
 */
export interface DisputeEvidenceView {
    id: number;
    evidence_type: string;
    title: string;
    description: string;
    file: string | null;
    created_at: string;
}

export interface DisputeDecisionView {
    decision_type: DecisionType;
    decision_type_label: string;
    comment: string;
    effective_refund_amount: string | null;
    refund_currency: string | null;
    decided_at: string;
}

export interface DisputeCaseView {
    public_id: string;
    reason: DisputeReason;
    reason_label: string;
    description: string;
    ui_status: string;
    technical_status: string;
    deadline_at: string | null;
    created_at: string;
    evidence: DisputeEvidenceView[];
    decision: DisputeDecisionView | null;
}

export interface EscalationHint {
    can_escalate_to_dispute: boolean;
    dispute_deadline_at: string | null;
    dispute_deadline_expired: boolean;
    requires_admin_override: boolean;
    dispute_unavailable_reason: DisputeUnavailableReason | null;
}

/**
 * The user-side detail returns only `{public_id}` for a parent ticket
 * (subject is intentionally omitted to keep the parent reference cheap —
 * the user can click through if they want the title).
 */
export interface ParentTicketRef {
    public_id: string;
}

/**
 * The user-facing detail is intentionally minimal. Lifecycle timestamps
 * (`resolved_at`, `closed_at`, `reopen_deadline_at`) are NOT exposed here
 * — backend enforces window logic and returns canonical error codes
 * (`reopen_window_expired`) on writes if the user is past the window.
 */
export interface TicketDetail extends TicketSummary {
    description: string;
    parent_ticket: ParentTicketRef | null;
    context: TicketContextSummary;
    /** Last N inline messages (up to 50 per design §5.4). */
    messages: MessageView[];
    dispute_case: DisputeCaseView | null;
    escalation: EscalationHint;
}

// ---------------------------------------------------------------------------
// Filters / argument shapes
// ---------------------------------------------------------------------------

export interface MyTicketsFilters {
    status?: TicketStatus[];
    category?: TicketCategory[];
    page?: number;
    page_size?: number;
}

export interface CreateTicketContext {
    type: TicketContextType;
    id: number | string;
}

export interface CreateTicketRequest {
    category: TicketCategory;
    subject: string;
    description: string;
    context?: CreateTicketContext | null;
    attachments?: File[];
    parent_ticket_public_id?: string | null;
}

export interface CreateTicketResult {
    ticket: TicketDetail;
    /** True when 200 OK (existing ticket returned by the duplicate-throttle). */
    duplicate: boolean;
    duplicate_code: string | null;
}

export interface PostMessagePayload {
    body: string;
    attachments?: File[];
}

export interface PostMessageResponse {
    message: MessageView;
    ticket_status: TicketStatus;
    ticket_status_changed: boolean;
    ticket_reopened: boolean;
}

export interface EscalatePayload {
    reason: DisputeReason;
    description: string;
}

export interface EscalateResponse {
    dispute_case: DisputeCaseView;
    ticket: { public_id: string; status: TicketStatus };
}

export interface AddDisputeEvidencePayload {
    title: string;
    description?: string;
    file: File;
}

export interface AddDisputeEvidenceResponse {
    evidence: DisputeEvidenceView;
    system_message_created: boolean;
    system_message_id: number | null;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown by all user support API helpers. Same shape as `SupportAdminApiError`
 * — wraps the original axios error but exposes a localised `message`, the
 * canonical backend `code` (when present), the HTTP status, and the structured
 * payload so callers can render code-specific UI.
 */
export class SupportApiError extends Error {
    public readonly status: number | undefined;
    public readonly code: string | null;
    public readonly data: unknown;
    public readonly fieldErrors: Record<string, string[]>;
    public readonly context: Record<string, unknown> | null;

    constructor(message: string, params: {
        status: number | undefined;
        code: string | null;
        data: unknown;
        fieldErrors: Record<string, string[]>;
        context: Record<string, unknown> | null;
    }) {
        super(message);
        this.name = 'SupportApiError';
        this.status = params.status;
        this.code = params.code;
        this.data = params.data;
        this.fieldErrors = params.fieldErrors;
        this.context = params.context;
    }

    public get isTicketClosed(): boolean {
        return this.code === 'ticket_closed';
    }

    public get isReopenWindowExpired(): boolean {
        return this.code === 'reopen_window_expired';
    }

    public get isDisputeNotOpen(): boolean {
        return this.code === 'dispute_not_open';
    }
}

const FALLBACK_ERROR_MESSAGE = 'Произошла ошибка. Попробуйте снова.';

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

// Canonical extractFieldErrors lives in `@/utils/extractFieldErrors` — see
// the `export { extractFieldErrors } from ...` near the top of this file.

const extractMessage = (data: unknown, fallback: string): string => {
    if (typeof data === 'string') {
        return data;
    }
    if (Array.isArray(data) && data.length > 0) {
        return String(data[0]);
    }
    if (isObject(data)) {
        if (typeof data.detail === 'string') {
            return data.detail;
        }
        if (Array.isArray(data.non_field_errors) && data.non_field_errors.length > 0) {
            return String(data.non_field_errors[0]);
        }
        const firstKey = Object.keys(data).find((key) => !['code', 'context', 'detail'].includes(key));
        if (firstKey) {
            const firstValue = data[firstKey];
            if (Array.isArray(firstValue) && firstValue.length > 0) {
                return String(firstValue[0]);
            }
            if (typeof firstValue === 'string') {
                return firstValue;
            }
        }
    }
    return fallback;
};

const wrapError = (error: unknown, fallback = FALLBACK_ERROR_MESSAGE): SupportApiError => {
    const axiosErr = error as AxiosError<unknown> | undefined;
    const response = axiosErr?.response;
    const status = response?.status;
    const data = response?.data;
    const code = isObject(data) && typeof data.code === 'string' ? data.code : null;
    const context = isObject(data) && isObject(data.context) ? data.context : null;
    const message = extractMessage(data, fallback);
    return new SupportApiError(message, {
        status,
        code,
        data,
        fieldErrors: extractFieldErrors(data),
        context,
    });
};

// ---------------------------------------------------------------------------
// Param serialisation helpers
// ---------------------------------------------------------------------------

const buildSearchParams = (filters: Record<string, unknown>): URLSearchParams => {
    const params = new URLSearchParams();
    for (const [key, rawValue] of Object.entries(filters)) {
        if (rawValue === undefined || rawValue === null || rawValue === '') {
            continue;
        }
        if (Array.isArray(rawValue)) {
            for (const item of rawValue) {
                if (item === undefined || item === null || item === '') {
                    continue;
                }
                params.append(key, String(item));
            }
        } else if (typeof rawValue === 'boolean') {
            params.append(key, rawValue ? 'true' : 'false');
        } else {
            params.append(key, String(rawValue));
        }
    }
    return params;
};

const appendAttachments = (form: FormData, files: File[] | undefined): void => {
    if (!files || files.length === 0) {
        return;
    }
    for (const file of files) {
        form.append('attachments', file, file.name);
    }
};

// ---------------------------------------------------------------------------
// Tickets — endpoints
// ---------------------------------------------------------------------------

/** GET /api/support/tickets/ */
export async function listMyTickets(
    filters: MyTicketsFilters = {},
): Promise<DRFPaginated<TicketSummary>> {
    try {
        const params = buildSearchParams(filters as Record<string, unknown>);
        const { data } = await api.get<DRFPaginated<TicketSummary>>(
            `support/tickets/?${params.toString()}`,
        );
        return data;
    } catch (error) {
        throw wrapError(error, 'Не удалось загрузить ваши обращения.');
    }
}

/** GET /api/support/tickets/{public_id}/ */
export async function getTicket(publicId: string): Promise<TicketDetail> {
    try {
        const { data } = await api.get<TicketDetail>(`support/tickets/${publicId}/`);
        return data;
    } catch (error) {
        throw wrapError(error, `Не удалось загрузить обращение ${publicId}.`);
    }
}

/**
 * POST /api/support/tickets/
 *
 * Backend semantics:
 *   - 201 → new ticket (`duplicate = false`).
 *   - 200 → throttled duplicate, existing ticket returned (`duplicate = true`,
 *     `duplicate_code` is e.g. `duplicate_active_ticket`).
 *
 * Both responses share the same body shape `{ ticket, ... }`. We branch on
 * the HTTP status to fill the boolean.
 */
export async function createTicket(
    req: CreateTicketRequest,
): Promise<CreateTicketResult> {
    try {
        const hasFiles = Array.isArray(req.attachments) && req.attachments.length > 0;
        const form = new FormData();
        form.append('category', req.category);
        form.append('subject', req.subject);
        form.append('description', req.description);
        if (req.context) {
            // Flat fields (NOT nested) per design §5.2 multipart variant.
            form.append('context.type', req.context.type);
            form.append('context.id', String(req.context.id));
        }
        if (req.parent_ticket_public_id) {
            form.append('parent_ticket_public_id', req.parent_ticket_public_id);
        }
        appendAttachments(form, req.attachments);

        // Always use multipart so the same payload shape covers the with-files
        // and without-files cases. `validateStatus` lets us treat 200 (duplicate)
        // and 201 (new) as success without throwing.
        const response = await api.post<{
            ticket: TicketDetail;
            duplicate?: boolean;
            duplicate_code?: string | null;
        }>(
            'support/tickets/',
            form,
            {
                headers: hasFiles
                    ? { 'Content-Type': 'multipart/form-data' }
                    : undefined,
                validateStatus: (status) => status === 200 || status === 201,
            },
        );

        const status = response.status;
        const body = response.data;
        const duplicate = status === 200 || body.duplicate === true;
        return {
            ticket: body.ticket,
            duplicate,
            duplicate_code: body.duplicate_code ?? null,
        };
    } catch (error) {
        throw wrapError(error, 'Не удалось создать обращение.');
    }
}

/** GET /api/support/tickets/{public_id}/messages/ */
export async function listMessages(
    publicId: string,
    page = 1,
    pageSize?: number,
): Promise<DRFPaginated<MessageView>> {
    try {
        const params = buildSearchParams({ page, page_size: pageSize });
        const { data } = await api.get<DRFPaginated<MessageView>>(
            `support/tickets/${publicId}/messages/?${params.toString()}`,
        );
        return data;
    } catch (error) {
        throw wrapError(error, 'Не удалось загрузить сообщения.');
    }
}

/** POST /api/support/tickets/{public_id}/messages/ */
export async function postMessage(
    publicId: string,
    body: string,
    attachments?: File[],
): Promise<PostMessageResponse> {
    try {
        const hasFiles = Array.isArray(attachments) && attachments.length > 0;
        if (hasFiles) {
            const form = new FormData();
            form.append('body', body);
            appendAttachments(form, attachments);
            const { data } = await api.post<PostMessageResponse>(
                `support/tickets/${publicId}/messages/`,
                form,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            return data;
        }
        const { data } = await api.post<PostMessageResponse>(
            `support/tickets/${publicId}/messages/`,
            { body },
        );
        return data;
    } catch (error) {
        throw wrapError(error, 'Не удалось отправить сообщение.');
    }
}

/** POST /api/support/tickets/{public_id}/escalate/ */
export async function escalateTicket(
    publicId: string,
    payload: EscalatePayload,
): Promise<EscalateResponse> {
    try {
        const { data } = await api.post<EscalateResponse>(
            `support/tickets/${publicId}/escalate/`,
            payload,
        );
        return data;
    } catch (error) {
        throw wrapError(error, 'Не удалось открыть спор.');
    }
}

/** POST /api/support/tickets/{public_id}/dispute-evidence/ */
export async function addDisputeEvidence(
    publicId: string,
    payload: AddDisputeEvidencePayload,
): Promise<AddDisputeEvidenceResponse> {
    try {
        const form = new FormData();
        form.append('title', payload.title);
        if (payload.description) {
            form.append('description', payload.description);
        }
        form.append('file', payload.file, payload.file.name);
        const { data } = await api.post<AddDisputeEvidenceResponse>(
            `support/tickets/${publicId}/dispute-evidence/`,
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return data;
    } catch (error) {
        throw wrapError(error, 'Не удалось загрузить доказательство.');
    }
}

// ---------------------------------------------------------------------------
// Convenience constants (Russian labels, mirroring `supportAdmin.ts` style)
// ---------------------------------------------------------------------------

export const TICKET_STATUSES: ReadonlyArray<{ value: TicketStatus; label: string }> = [
    { value: 'open', label: 'Открыто' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'waiting_user', label: 'Ждём вас' },
    { value: 'dispute_opened', label: 'Открыт спор' },
    { value: 'resolved', label: 'Решено' },
    { value: 'closed', label: 'Закрыто' },
];

export const TICKET_CATEGORIES: ReadonlyArray<{ value: TicketCategory; label: string; description: string }> = [
    { value: 'tech', label: 'Техническая проблема', description: 'Не работает функция, ошибка интерфейса.' },
    { value: 'payment', label: 'Оплата и возврат', description: 'Списание, возврат, чек.' },
    { value: 'booking', label: 'Бронирование', description: 'Запись на сессию или перенос.' },
    { value: 'consultation_quality', label: 'Проблема с личной сессией', description: 'Эксперт не пришёл, опоздал, качество.' },
    { value: 'complaint', label: 'Жалоба или спор', description: 'Серьёзная жалоба, требуется разбор.' },
    { value: 'other', label: 'Другое', description: 'Что-то ещё.' },
];

export const DISPUTE_REASONS: ReadonlyArray<{ value: DisputeReason; label: string }> = [
    { value: 'expert_no_show', label: 'Эксперт не пришёл' },
    { value: 'client_no_show', label: 'Клиент не пришёл' },
    { value: 'expert_late', label: 'Эксперт сильно опоздал' },
    { value: 'expert_quality', label: 'Качество личной сессии' },
    { value: 'session_interrupted', label: 'Сессия прервана' },
    { value: 'not_as_described', label: 'Не соответствует описанию' },
    { value: 'payment_issue', label: 'Проблема с оплатой' },
    { value: 'other', label: 'Другое' },
];

/**
 * Canonical Russian copy for `escalation.dispute_unavailable_reason`. The
 * mapping is exhaustive — adding a new reason on the backend without updating
 * this map will fall back to a generic message.
 */
export const DISPUTE_UNAVAILABLE_REASONS: Readonly<Record<DisputeUnavailableReason, string>> = {
    no_financial_object: 'Это обращение не связано с оплатой, поэтому спор открыть нельзя.',
    deadline_unknown: 'Срок открытия спора нельзя определить. Если ситуация серьёзная — напишите оператору.',
    deadline_expired: 'Срок самостоятельного открытия спора истёк (48 часов после личной сессии). Напишите оператору, и мы разберёмся.',
    already_escalated: 'По этому обращению уже открыт спор.',
    existing_open_dispute: 'По этому объекту уже открыт спор.',
    ticket_closed: 'Обращение закрыто. Создайте новое обращение, если проблема не решена.',
    permission_denied: 'Открыть спор может только автор обращения.',
};

/** Russian explanations for canonical context types — used in the modal step 2. */
export const CONTEXT_TYPE_LABELS: Readonly<Record<TicketContextType, string>> = {
    // TODO(v2-cutover): legacy — see TicketContextType note. The V2 anchor for a
    // личная сессия is the 'order_item' context.
    consultation_booking: 'Личная сессия',
    conference: 'Видеозвонок',
    legacy_reservation: 'Бронирование',
    commerce_reservation: 'Бронирование',
    secure_deal: 'Безопасная сделка',
    order_item: 'Платёж',
    secure_payment: 'Платёж',
    secure_refund: 'Возврат',
};

export const labelFor = <T extends string>(
    value: T,
    list: ReadonlyArray<{ value: T; label: string }>,
): string => {
    const match = list.find((item) => item.value === value);
    return match ? match.label : String(value);
};

/**
 * Resolve the user-facing message describing why escalation is blocked. Falls
 * back to a generic phrase if the backend returned an unknown reason code.
 */
export const disputeUnavailableMessage = (
    reason: DisputeUnavailableReason | null,
): string => {
    if (!reason) {
        return '';
    }
    return DISPUTE_UNAVAILABLE_REASONS[reason]
        ?? 'Открыть спор сейчас нельзя. Напишите оператору, и мы поможем.';
};

// ---------------------------------------------------------------------------
// Frontend-side attachment validation (matches backend §3.5 invariants).
// ---------------------------------------------------------------------------

export const SUPPORT_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const SUPPORT_ATTACHMENT_MAX_PER_MESSAGE = 10;

export const SUPPORT_ATTACHMENT_ACCEPT = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
].join(',');

const FORBIDDEN_EXTENSIONS = new Set([
    '.exe', '.sh', '.bat', '.cmd', '.scr', '.ps1', '.js', '.vbs', '.dll', '.msi',
]);

/**
 * Pre-flight client-side validation. Mirrors the backend rules so users see
 * fast feedback. The server still performs authoritative validation including
 * magic-byte sniffing on `video/mp4`.
 */
export const validateAttachment = (file: File): string | null => {
    if (file.size > SUPPORT_ATTACHMENT_MAX_BYTES) {
        return `Файл «${file.name}» больше 25 МБ.`;
    }
    if (file.type === 'video/mp4') {
        return `Видео «${file.name}» нельзя прикрепить. Загрузите кадр или ссылку на запись.`;
    }
    const lower = file.name.toLowerCase();
    for (const ext of FORBIDDEN_EXTENSIONS) {
        if (lower.endsWith(ext)) {
            return `Файлы с расширением ${ext} не разрешены.`;
        }
    }
    return null;
};

/**
 * Validate a candidate list of attachments. Returns first error message, or
 * `null` if all files are acceptable.
 */
export const validateAttachmentList = (files: File[]): string | null => {
    if (files.length > SUPPORT_ATTACHMENT_MAX_PER_MESSAGE) {
        return `Можно прикрепить не больше ${SUPPORT_ATTACHMENT_MAX_PER_MESSAGE} файлов.`;
    }
    for (const file of files) {
        const err = validateAttachment(file);
        if (err) return err;
    }
    return null;
};
